import { Combobox, InputBase, useCombobox } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useClients } from "#modules/clients/view/useClients";
import { useCreateClient } from "#modules/clients/view/useCreateClient";
import { ClientAtom } from "#shared/view/components/AppShell/Atoms";
import { Flex } from "#shared/view/components/Flex";

export function SelectClientComboBox({
  onSaveClient,
  currentClient,
  setCurrentClient,
  onCancel,
}: {
  onSaveClient: (foundClient: ClientAtom) => void;
  currentClient: ClientAtom;
  setCurrentClient: React.Dispatch<React.SetStateAction<ClientAtom>>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const { clients: allClients } = useClients();
  const { createClient } = useCreateClient({
    options: {
      onSuccess: (client) => onSaveClient(client),
    },
  });

  const clients: ClientAtom[] = allClients.map((client) => {
    return {
      id: client.id,
      name: client.name,
    };
  });

  const [search, setSearch] = useState(currentClient?.name ?? "");

  const exactOptionMatch = allClients.some((item) => item.name === search);

  const filteredOptions = exactOptionMatch
    ? clients
    : clients.filter((item) => item?.name.toLowerCase().includes(search.toLowerCase().trim()));

  const options = filteredOptions.map((item) => (
    <Combobox.Option
      value={item?.id ?? ""}
      key={item?.name}
      active={item?.id === currentClient?.id}
    >
      <Flex gap={"xxs"} align={"center"}>
        {item?.id === currentClient?.id && <IconCheck size={12} />}
        <span>{item?.name}</span>
      </Flex>
    </Combobox.Option>
  ));

  const handleSelectClient = (val: string) => {
    const foundClient = clients.find((item) => item?.id === val);

    if (foundClient) {
      setCurrentClient(foundClient);
      onSaveClient(foundClient);
    } else {
      onCancel();
    }
  };

  const handleAddClient = () => {
    createClient({ name: search });
  };

  return (
    <Combobox
      store={combobox}
      withinPortal={true}
      onOptionSubmit={(val) => {
        if (val === "addClient") {
          handleAddClient();
        } else {
          handleSelectClient(val);
        }
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <InputBase
          autoFocus
          size="xs"
          px="xs"
          pointer
          rightSection={<Combobox.Chevron size="1rem" />}
          value={search}
          onKeyUp={(e) => {
            if (e.key === "Escape") {
              onCancel();
            }
          }}
          onFocus={() => combobox.openDropdown()}
          onClick={() => combobox.toggleDropdown()}
          onChange={(event) => {
            combobox.openDropdown();
            setSearch(event.currentTarget.value);
          }}
          placeholder={t("client.searchPlaceholder")}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options mah={240} style={{ overflowY: "auto" }}>
          {options}
        </Combobox.Options>

        {!exactOptionMatch && search.trim().length > 0 && (
          <Combobox.Option value="addClient" key={"emptyOption"}>
            <Combobox.Empty>{t("client.addClient", { name: search })}</Combobox.Empty>
          </Combobox.Option>
        )}
      </Combobox.Dropdown>
    </Combobox>
  );
}
